import { useMemo } from 'react';
import { Camera, Clock, Info } from 'lucide-react';
import { cn } from '@/utils/cn';
import { formatTooth } from '@/utils/toothNotation';
import type { ToothData, ToothNotation } from './types';
import { getPrimaryStatus } from './types';
import { useDentalPalette, resolveConditionStyle, HEALTHY_STYLE } from './dentalColors';

// SVG templates imported as raw strings — no SVGR dependency, no npm install.
// Vite bundles them as JS string literals; they gzip to ~10% of raw size.
import svg11 from './teeth-svgs/11.svg?raw';
import svg13 from './teeth-svgs/13.svg?raw';
import svg14 from './teeth-svgs/14.svg?raw';
import svg16 from './teeth-svgs/16.svg?raw';
import svgNoSel from './teeth-svgs/icon_no_selection.svg?raw';

// ── Props ─────────────────────────────────────────────────────────────────────

interface ToothProps {
  tooth?: ToothData;
  fdi: string;
  selected: boolean;
  notation: ToothNotation;
  onClick: (event: React.MouseEvent) => void;
  readOnly?: boolean;
  hasImage?: boolean;
  hasPlan?: boolean;
  hasNote?: boolean;
}

// ── ZoliQua SVG layer system (React-Odontogram-Modul v2.1.1) ─────────────────
//
// Each buccal template has viewBox="0 0 36 64".
// Structure: alveolar bone + gum at top (y 0–17), tooth root (y 0–35),
// tooth crown + incisal/occlusal edge at bottom (y 35–64).
// The embedded <style> rule [data-active="0"] { display: none; } hides inactive
// layers. Layers visible by default: base (bone/gum), tooth (enamel + healthy
// pulp). Layers hidden by default (style="display:none;"): mods
// (periodontium/inflammation/mobility), tooth-variants (broken/crownprep),
// endos (RCT fills, pins, resections), surfaces (fillings, caries),
// restorations (implant, crown, bridge, veneer), milktooth.
// TAND-11 will map ToothStatusTypes to these layer IDs.
// TAND-10 renders the healthy base and adds a crown tint overlay.

// ── Native orientation and per-quadrant transforms ────────────────────────────
//
// ZoliQua SVGs are drawn in Q1 perspective (FDI 11 = upper-right central
// incisor): root at top, crown at bottom, mesial surface on the right side.
// Confirmed via surface IDs: filling-amalgam-mesial is at x≈25–30 (right
// side of 36px viewBox), filling-amalgam-distal is at x≈8–12 (left side).
//
// Per-quadrant CSS transform applied to the SVG wrapper:
//   Q1 upper right (FDI 1x / primary 5x): no transform   — native orientation
//   Q2 upper left  (FDI 2x / primary 6x): scaleX(-1)     — mesial/distal mirror
//   Q3 lower left  (FDI 3x / primary 7x): scale(-1, -1)  — arch flip + mirror
//   Q4 lower right (FDI 4x / primary 8x): scaleY(-1)     — arch flip only

type Quadrant = 1 | 2 | 3 | 4;

function getQuadrant(fdi: string): Quadrant {
  const q = parseInt(fdi[0], 10);
  if (q === 1 || q === 5) return 1;
  if (q === 2 || q === 6) return 2;
  if (q === 3 || q === 7) return 3;
  return 4;
}

function getSvgTemplate(fdi: string): string {
  const d2 = parseInt(fdi[1], 10);
  if (d2 <= 2) return svg11;  // central and lateral incisors
  if (d2 === 3) return svg13; // canines
  if (d2 <= 5) return svg14;  // premolars (and primary posterior teeth)
  return svg16;               // molars (including third molars)
}

function getQuadrantTransform(q: Quadrant): string | undefined {
  if (q === 2) return 'scaleX(-1)';
  if (q === 3) return 'scale(-1, -1)';
  if (q === 4) return 'scaleY(-1)';
  return undefined;
}

// ── SVG preparation ───────────────────────────────────────────────────────────
//
// Prefix every id="..." and url(#...) reference with a per-tooth uid so that
// gradient/filter/clipPath definitions don't bleed between instances sharing
// the same template (e.g., all 8 molars reuse 16.svg but need separate
// gradient namespaces). Also strips the XML declaration and sets width="100%".

function prepareSvg(raw: string, uid: string): string {
  return raw
    .replace(/<\?xml[^?]*\?>/g, '')
    .replace(/\bid="([^"]+)"/g, `id="${uid}-$1"`)
    .replace(/url\(#([^)]+)\)/g, `url(#${uid}-$1)`)
    .replace(/href="#([^"]+)"/g, `href="#${uid}-$1"`)
    .replace(/<svg([^>]*)>/, '<svg$1 width="100%" height="auto">')
    .trimStart();
}

// ── Component ─────────────────────────────────────────────────────────────────

export function Tooth({ tooth, fdi, selected, notation, onClick, readOnly, hasImage, hasPlan, hasNote }: ToothProps) {
  const displayNumber = formatTooth(fdi, notation);
  const palette = useDentalPalette();
  const quadrant = getQuadrant(fdi);
  const isUpper = quadrant === 1 || quadrant === 2;

  const isMissing = tooth?.status?.includes('missing') || tooth?.tooth_type === 'missing';

  const primaryCondition = useMemo(
    () => getPrimaryStatus(tooth?.status ?? ['healthy']),
    [tooth?.status],
  );

  const condStyle = primaryCondition === 'healthy'
    ? HEALTHY_STYLE
    : resolveConditionStyle(primaryCondition, palette);

  const isHealthy = primaryCondition === 'healthy';

  // Memoize SVG processing (regex is expensive on ~50KB strings)
  const processedSvg = useMemo(
    () => prepareSvg(isMissing ? svgNoSel : getSvgTemplate(fdi), `t${fdi}`),
    [fdi, isMissing],
  );

  const svgTransform = getQuadrantTransform(quadrant);

  const badgeSummary = [hasImage && 'billede', hasPlan && 'behandlingsplan', hasNote && 'note']
    .filter(Boolean).join(', ');
  const ariaLabel = [`Tand ${displayNumber}`, primaryCondition, badgeSummary].filter(Boolean).join(', ');

  // ── Missing tooth ──────────────────────────────────────────────────────────
  if (isMissing) {
    return (
      <div className="flex flex-col items-center gap-0.5">
        <button
          type="button"
          onClick={onClick}
          disabled={readOnly}
          title={`Tand ${displayNumber} — Mangler`}
          aria-label={ariaLabel}
          className={cn(
            'relative w-[52px] rounded transition-all duration-150',
            selected && 'ring-2 ring-ds-teal ring-offset-1',
            !readOnly && 'cursor-pointer hover:opacity-75',
          )}
        >
          <div dangerouslySetInnerHTML={{ __html: processedSvg }} />
        </button>
        <span className="text-[10px] font-medium text-ds-muted">{displayNumber}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-0.5 group">
      <button
        type="button"
        onClick={onClick}
        disabled={readOnly}
        title={`Tand ${displayNumber}`}
        aria-label={ariaLabel}
        className={cn(
          'relative w-[52px] rounded-sm transition-all duration-150',
          selected && 'ring-2 ring-ds-teal ring-offset-1 rounded',
          !readOnly && 'cursor-pointer hover:-translate-y-0.5',
        )}
      >
        {/* Anatomical SVG — flipped/mirrored via CSS transform per quadrant */}
        <div
          style={{
            transform: svgTransform,
            filter: selected
              ? 'drop-shadow(0 0 5px rgba(99,102,241,0.5))'
              : 'drop-shadow(0 1.5px 2.5px rgba(28,25,23,0.12))',
          }}
          dangerouslySetInnerHTML={{ __html: processedSvg }}
        />

        {/* Condition tint: semi-transparent ellipse over the crown area.
            Crown occupies the bottom ~46% of the viewBox for upper teeth;
            after scaleY(-1) it occupies the top ~46% for lower teeth.
            The overlay div sits outside the flipped SVG wrapper so it is
            not affected by the quadrant transform. */}
        {!isHealthy && (
          <div
            className={cn(
              'pointer-events-none absolute left-[5%] w-[90%] rounded-[50%]',
              isUpper ? 'bottom-[3%]' : 'top-[3%]',
            )}
            style={{ height: '46%', backgroundColor: condStyle.crown, opacity: 0.45 }}
          />
        )}

        {/* Badge icons */}
        {hasPlan && (
          <span className="pointer-events-none absolute top-0 left-0 z-10 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-400 text-white">
            <Clock className="h-2 w-2" />
          </span>
        )}
        {hasImage && (
          <span className="pointer-events-none absolute top-0 right-0 z-10 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-ds-teal text-ds-on-accent">
            <Camera className="h-2 w-2" />
          </span>
        )}
        {hasNote && (
          <span className="pointer-events-none absolute bottom-5 right-0 z-10 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-ds-muted text-ds-on-accent">
            <Info className="h-2 w-2" />
          </span>
        )}
      </button>

      <span
        className={cn(
          'text-[10px] font-semibold tracking-tight transition-colors',
          selected ? 'text-ds-teal' : 'text-ds-muted group-hover:text-ds-text',
        )}
      >
        {displayNumber}
      </span>
    </div>
  );
}
